import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Card, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import userAdminService from "../../services/userAdminService";
import { FaRegTrashAlt } from "react-icons/fa";
const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== "new";
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    user_type: "admin",
    is_active: true,
    password: "",
    image_url: "",
  });
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [fileInputKey, setFileInputKey] = useState(0);

  useEffect(() => {
    const load = async () => {
      if (isEdit) {
        try {
          const u = await userAdminService.get(id);
          setForm({
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            email: u.email || "",
            phone: u.phone || "",
            user_type: u.user_type || "admin",
            is_active: u.is_active !== false,
            password: "",
            image_url: u.image_url || "",
          });
        } catch (e) {
          toast.error("Failed to load user");
        }
      }
    };
    load();
  }, [id, isEdit]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setImageError("Please select a valid image file");
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setImageError("Image must be less than 5MB");
        return;
      }
      setAvatar(file);
      setImageError("");
    } else {
      setAvatar(null);
    }
  };

  const handleRemoveImage = () => {
    setAvatar(null);
    setFileInputKey((prev) => prev + 1); // Reset file input
    if (isEdit) {
      setForm((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let saved;
      if (isEdit) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        saved = await userAdminService.update(id, payload);
      } else {
        if (!form.password) {
          toast.error("Password is required for new user");
          setLoading(false);
          return;
        }
        saved = await userAdminService.create(form);
      }
      if (avatar) {
        await userAdminService.uploadImage(saved.id || id, avatar);
      }
      toast.success("User saved");
      navigate("/admin/users");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
        <h5 className="mb-0">{isEdit ? "Edit User" : "Create User"}</h5>
        <div className="d-flex justify-content-end gap-2">
          <Button type="submit" form="user-form" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="btn btn-danger btn-sm"
            onClick={() => navigate("/admin/users")}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </div>
      <Card>
        <Card.Body>
          <Form id="user-form" onSubmit={onSubmit}>
            <Row>
              <Col md={3}>
                <div className="d-flex flex-column align-items-center">
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: 48,
                      overflow: "hidden",
                      background: "#f1f1f1",
                      marginBottom: 8,
                    }}
                  >
                    <img
                      src={
                        avatar
                          ? URL.createObjectURL(avatar)
                          : form?.image_url || "/not-img-found.png"
                      }
                      alt="avatar"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/not-img-found.png";
                      }}
                    />
                  </div>
                  <Form.Group className="mb-3" style={{ width: "100%" }}>
                    {(avatar || form?.image_url) && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="mt-2"
                        onClick={handleRemoveImage}
                        disabled={loading}
                        style={{
                          float: "right",
                          marginBottom: "6px",
                        }}
                      >
                        <FaRegTrashAlt />
                      </Button>
                    )}
                    <Form.Label>Profile Image</Form.Label>
                    <Form.Control
                      id="file-uplad"
                      key={fileInputKey}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      disabled={loading}
                      isInvalid={!!imageError}
                    />
                    <Form.Control.Feedback type="invalid">
                      {imageError}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              </Col>
              <Col md={9}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        name="first_name"
                        value={form.first_name}
                        onChange={onChange}
                        placeholder="Enter first name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        name="last_name"
                        value={form.last_name}
                        onChange={onChange}
                        placeholder="Enter last name"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="Enter email address"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="Enter phone number"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>User Type</Form.Label>
                      <Form.Select
                        name="user_type"
                        value={form.user_type}
                        onChange={onChange}
                      >
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Check
                        type="switch"
                        label={form.is_active ? "Active" : "Inactive"}
                        checked={form.is_active}
                        name="is_active"
                        onChange={onChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {!isEdit && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={form.password}
                          onChange={onChange}
                          placeholder="Enter password"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserForm;
